import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label", "innerHTML"]
const _hoisted_2 = ["src"]
const _hoisted_3 = {
  key: 0,
  class: "mode-toggle"
}
const _hoisted_4 = { class: "field-label" }
const _hoisted_5 = { class: "modal-buttons" }
const _hoisted_6 = { class: "operators" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = ["placeholder"]
const _hoisted_10 = { class: "preview-label" }
const _hoisted_11 = ["innerHTML"]
const _hoisted_12 = {
  key: 0,
  class: "latex-error"
}
const _hoisted_13 = { class: "modal-buttons" }

import { computed, watch, ref, nextTick, onMounted, onBeforeUnmount, toRefs } from "vue";
import katex from "katex";
import "katex/dist/katex.min.css";
import "mathlive";
import { getComputedPageLayout, getIcon, IPageLayout } from "@/helpers/api";
import { useDeviceStatus } from "@/stores/deviceStatus";

type Operator = [string, string];
type OperatorGroup = Operator[];
type OperatorsMatrix = OperatorGroup[];

interface _1015 extends IPageLayout {
  labels: {
    latex: string;
    title: string;
    cancel: string;
    insert: string;
    formula: string;
    preview: string;
  };
  helps: {
    formula: string;
  };
  commands: {
    latex?: OperatorsMatrix;
  };
}

const DOCK_GRIP = 44;
const DOCK_MARGIN = 8;

const chevronRightSvg = `
<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <polyline points="9 18 15 12 9 6"></polyline>
</svg>
`;

const chevronLeftSvg = `
<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <polyline points="15 18 9 12 15 6"></polyline>
</svg>
`;


export default /*@__PURE__*/_defineComponent({
  __name: 'ModalEditor',
  props: {
    modelValue: {},
    visible: { type: Boolean },
    operators: {},
    fromModal: { type: Boolean },
    teleportTarget: {},
    mathLiveOptions: {}
  },
  emits: ["update:modelValue", "close", "confirm"],
  setup(__props: any, { emit: __emit }) {

const layout = getComputedPageLayout<_1015>("1015");

const deviceStatus = useDeviceStatus();
const { platform } = toRefs(deviceStatus);

const closeIcon = ref<string>();

onMounted(async () => {
  closeIcon.value = await getIcon("1010", "close");
});

const props = __props;

const emit = __emit;

const latexValue = computed({
  get: () => props.modelValue,
  set: (val: string) => emit("update:modelValue", val),
});

const operatorsS = computed<OperatorsMatrix>(() => {
  if (props.operators && props.operators.length) return props.operators;
  return [];
});

const latexHtml = ref<string>("");
const latexError = ref<string>("");
const latexMode = ref<boolean>(false);

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const mathFieldEl = ref<any | null>(null);
const modalEl = ref<HTMLElement | null>(null);

const mathLiveOptions = computed(() => props.mathLiveOptions ?? {});

const showLatexMode = computed(
  () => mathLiveOptions.value.showLatexMode ?? true
);

const showMenuToggle = computed(
  () => mathLiveOptions.value.showMenuToggle ?? true
);

const showVirtualKeyboard = computed(
  () => mathLiveOptions.value.showVirtualKeyboard ?? true
);

const backendShowVirtualKeyboard = computed(
  () => mathLiveOptions.value.showVirtualKeyboard ?? true
);

const canUseVirtualKeyboard = computed(() => {
  if (platform.value === "mobile") return backendShowVirtualKeyboard.value;
  return false;
});



const shouldUseVirtualKeyboard = computed(() => {
  return backendShowVirtualKeyboard.value;
});

const mathVirtualKeyboardPolicy = computed(() => {
  if (!backendShowVirtualKeyboard.value) return "sandboxed";

  return "manual";
});
const showVirtualKeyboardToggle = computed(() => {
  return backendShowVirtualKeyboard.value;
});

const hasAutoOpenedKeyboard = ref(false);

function showVirtualKeyboardOnceOnMobile() {
  if (platform.value !== "mobile") return;
  if (!backendShowVirtualKeyboard.value) return;
  if (hasAutoOpenedKeyboard.value) return;

  hasAutoOpenedKeyboard.value = true;

  setTimeout(() => {
    const vk = getVirtualKeyboard();

    prepareMathFieldForVirtualKeyboard();

    requestAnimationFrame(() => {
      try {
        vk?.show?.();
      } catch (error) {
        console.warn("Erro ao abrir teclado virtual:", error);
      }
    });
  }, 120);
}

const keyboardLayouts = computed(
  () =>
    mathLiveOptions.value.keyboardLayouts ?? [
      "alphabetic",
      "numeric",
      "symbols",
      "greek",
    ]
);

watch(
  showLatexMode,
  (enabled) => {
    if (!enabled) {
      latexMode.value = false;
    }
  },
  { immediate: true }
);

const keyboardHeight = ref(0);

function getVirtualKeyboard(): any | null {
  return (window as any).mathVirtualKeyboard ?? null;
}

function renderOp(latex: string): string {
  try {
    return katex.renderToString(latex, { throwOnError: false });
  } catch {
    return latex;
  }
}

function syncFromMathField() {
  if (mathFieldEl.value) {
    latexValue.value = mathFieldEl.value.value || "";
  }
}

function applyPageKeyboardInset(height: number) {
  document.documentElement.style.setProperty("--vk-height", `${height}px`);
  document.body.style.setProperty("--vk-height", `${height}px`);
}

function clearPageKeyboardInset() {
  document.documentElement.style.removeProperty("--vk-height");
  document.body.style.removeProperty("--vk-height");
}

let geometryChangeTimer: ReturnType<typeof setTimeout> | null = null;

function applyKeyboardGeometry() {
  const vk = getVirtualKeyboard();
  const height = vk?.boundingRect?.height || 0;

  keyboardHeight.value = height;
  applyPageKeyboardInset(height);

  if (platform.value === "mobile") {
    keyboardLift.value = 0;
    return;
  }

  nextTick(() => {
    updateModalLift();
  });
}

function handleGeometryChange() {
  if (geometryChangeTimer) {
    clearTimeout(geometryChangeTimer);
    geometryChangeTimer = null;
  }

  if (platform.value === "mobile") {
    geometryChangeTimer = setTimeout(() => {
      applyKeyboardGeometry();
      geometryChangeTimer = null;
    }, 120);

    return;
  }

  applyKeyboardGeometry();
}

const vkAttachedForSession = ref(false);

function attachVirtualKeyboard() {
  const vk = getVirtualKeyboard();
  if (!vk) return;

  vk.container = document.body;
  vk.alphabeticLayout = "qwerty";
  vk.layouts = keyboardLayouts.value;

  vk.removeEventListener?.("geometrychange", handleGeometryChange);

  if (shouldUseVirtualKeyboard.value) {
    vk.addEventListener?.("geometrychange", handleGeometryChange);
  } else {
    vk.hide?.();
    keyboardHeight.value = 0;
    keyboardLift.value = 0;
    clearPageKeyboardInset();
  }

  vkAttachedForSession.value = true;
}

const keyboardLift = ref(0);

function updateModalLift() {
  const el = modalEl.value;
  if (!el) {
    keyboardLift.value = 0;
    return;
  }

  const rect = el.getBoundingClientRect();
  const topGap = platform.value === "mobile" ? 8 : 20;
  const bottomGap = platform.value === "mobile" ? 8 : 20;

  const visibleBottom = window.innerHeight - keyboardHeight.value - bottomGap;


  const neededLift = Math.max(0, rect.bottom - visibleBottom);

  const maxLift = Math.max(0, rect.top - topGap);

  keyboardLift.value = Math.min(neededLift, maxLift);
}

function detachVirtualKeyboard() {
  const vk = getVirtualKeyboard();

  if (geometryChangeTimer) {
    clearTimeout(geometryChangeTimer);
    geometryChangeTimer = null;
  }

  if (vk) {
    vk.removeEventListener?.("geometrychange", handleGeometryChange);

    try {
      vk.hide?.();
    } catch (error) {
      console.warn("Erro ao fechar teclado virtual:", error);
    }
  }

  keyboardHeight.value = 0;
  keyboardLift.value = 0;
  clearPageKeyboardInset();
  vkAttachedForSession.value = false;
}

const boundingBox = computed(() => modalEl.value?.getBoundingClientRect());



function initMathField() {
  if (!mathFieldEl.value) return;

  const el = mathFieldEl.value;

  el.value = latexValue.value || "";
  el.mathVirtualKeyboardPolicy = mathVirtualKeyboardPolicy.value;

  requestAnimationFrame(() => {
    attachVirtualKeyboard();
    el.focus?.();

    showVirtualKeyboardOnceOnMobile();
  });
}

function updatePreview() {
  const val = latexValue.value;

  if (!latexMode.value) {
    latexHtml.value = "";
    latexError.value = "";
    return;
  }

  try {
    latexHtml.value = katex.renderToString(val, { throwOnError: false });
    latexError.value = "";
  } catch {
    latexHtml.value = val;
    latexError.value = "Erro ao renderizar";
  }
}

watch(
  () => latexValue.value,
  () => {
    updatePreview();
  },
  { immediate: true }
);

watch(
  () => latexMode.value,
  async (isLatex) => {
    updatePreview();

    if (!isLatex) {
      await nextTick();
      initMathField();
    } else {
      detachVirtualKeyboard();
      await nextTick();
      textareaRef.value?.focus();
    }
  }
);

watch(
  () => props.visible,
  async (v) => {
    if (v && !latexMode.value) {
      hasAutoOpenedKeyboard.value = false;

      await nextTick();
      initMathField();
    } else if (!v) {
      hasAutoOpenedKeyboard.value = false;
      detachVirtualKeyboard();
    }
  }
);

watch(
  () => props.modelValue,
  (val) => {
    if (!latexMode.value && mathFieldEl.value) {
      const el = mathFieldEl.value;
      if (el.value !== val) {
        el.value = val || "";
      }
    }
  }
);

function insertOperator(template: string) {
  if (!latexMode.value && mathFieldEl.value) {
    const el = mathFieldEl.value;

    if (typeof el.insert === "function") {
      el.insert(template, {
        selectionMode: "placeholder",
        focus: true,
        format: "latex",
      });
    } else if (typeof el.executeCommand === "function") {
      el.executeCommand([
        "insert",
        template,
        {
          selectionMode: "placeholder",
          focus: true,
          format: "latex",
        },
      ]);
    } else {
      el.value = (el.value || "") + template;
    }

    el.focus?.();
    syncFromMathField();
    return;
  }

  const ta = textareaRef.value;
  const current = latexValue.value || "";

  if (!ta) {
    latexValue.value = current + template;
    return;
  }

  const start = ta.selectionStart ?? current.length;
  const end = ta.selectionEnd ?? start;

  const before = current.slice(0, start);
  const after = current.slice(end);

  latexValue.value = before + template + after;

  nextTick(() => {
    const pos = start + template.length;
    ta.focus();
    ta.setSelectionRange(pos, pos);
  });
}

function handleModalKeydown(event: KeyboardEvent) {
  if (latexMode.value) return;
  if (platform.value === "mobile") return;
  if (event.key !== "Enter") return;
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;

  event.preventDefault();
  event.stopPropagation();
  onConfirm();
}

function onClose() {
  detachVirtualKeyboard();
  emit("close");
}

function onConfirm() {
  detachVirtualKeyboard();
  emit("confirm", latexValue.value);
}

onBeforeUnmount(() => {
  detachVirtualKeyboard();
});

const isDocked = ref(false);

const savedDx = ref(0);
const savedDy = ref(0);

const dx = ref(0);
const dy = ref(0);

const modalStyle = computed(() => {
  const lift = platform.value === "mobile" ? 0 : keyboardLift.value;

  return {
    transform: `translate3d(${dx.value}px, calc(${dy.value}px - ${lift}px), 0)`,
    "--bounding-top": `${boundingBox.value?.top ?? 0}px`,
  };
});

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function getDockBounds() {
  if (props.teleportTarget instanceof HTMLElement) {
    const rect = props.teleportTarget.getBoundingClientRect();

    return {
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
  }

  return {
    left: 0,
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

function toggleDock() {
  const el = modalEl.value;
  if (!el) return;

  if (!isDocked.value) {
    savedDx.value = dx.value;
    savedDy.value = dy.value;

    const rect = el.getBoundingClientRect();
    const bounds = getDockBounds();

    const targetLeft = bounds.right - DOCK_GRIP - DOCK_MARGIN;
    const targetTop = clamp(
      rect.top,
      bounds.top + DOCK_MARGIN,
      bounds.bottom - DOCK_GRIP - DOCK_MARGIN
    );

    dx.value += targetLeft - rect.left;
    dy.value += targetTop - rect.top;

    isDocked.value = true;
  } else {
    dx.value = savedDx.value;
    dy.value = savedDy.value;
    isDocked.value = false;
  }
}

function prepareMathFieldForVirtualKeyboard() {
  if (!backendShowVirtualKeyboard.value) return;
  if (!mathFieldEl.value) return;

  const el = mathFieldEl.value;

  el.mathVirtualKeyboardPolicy = "manual";

  // Re-attaching on every tap rebuilt the shared vk and could snap its layer back to alphabetic.
  if (!vkAttachedForSession.value) {
    attachVirtualKeyboard();
  }

  requestAnimationFrame(() => {
    el.focus?.();
  });
}

return (_ctx: any,_cache: any) => {
  const _component_math_field = _resolveComponent("math-field")!

  return (_openBlock(), _createBlock(_Teleport, {
    to: props.teleportTarget || 'body'
  }, [
    (_ctx.visible)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "modal-backdrop",
          onClick: _withModifiers(onClose, ["self"])
        }, [
          _createElementVNode("div", {
            ref_key: "modalEl",
            ref: modalEl,
            class: _normalizeClass(["modal", { 'is-docked': isDocked.value }]),
            style: _normalizeStyle(modalStyle.value),
            onKeydownCapture: handleModalKeydown
          }, [
            _createElementVNode("button", {
              type: "button",
              class: "dock-handle",
              onPointerdown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
              onClick: _withModifiers(toggleDock, ["stop"]),
              "aria-label": isDocked.value ? 'Mostrar' : 'Recolher',
              innerHTML: isDocked.value ? chevronLeftSvg : chevronRightSvg
            }, null, 40, _hoisted_1),
            _createElementVNode("h3", null, _toDisplayString(_unref(layout)?.labels.title), 1),
            _createElementVNode("img", {
              src: closeIcon.value,
              class: "close-icon",
              onPointerdown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
              onClick: _withModifiers(onClose, ["stop"])
            }, null, 40, _hoisted_2),
            (showLatexMode.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("label", null, [
                    _createElementVNode("span", null, _toDisplayString(_unref(layout)?.labels.latex), 1),
                    _withDirectives(_createElementVNode("input", {
                      type: "checkbox",
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((latexMode).value = $event))
                    }, null, 512), [
                      [_vModelCheckbox, latexMode.value]
                    ]),
                    _cache[4] || (_cache[4] = _createElementVNode("span", { class: "toggle-switch" }, [
                      _createElementVNode("span", { class: "toggle-knob" })
                    ], -1))
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(layout)?.labels.formula), 1),
            (!latexMode.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createVNode(_component_math_field, {
                    ref_key: "mathFieldEl",
                    ref: mathFieldEl,
                    onInput: syncFromMathField,
                    onPointerdownCapture: prepareMathFieldForVirtualKeyboard,
                    onFocus: prepareMathFieldForVirtualKeyboard,
                    class: _normalizeClass(["mathlive-field", {
              'hide-menu-toggle': !showMenuToggle.value,
              'hide-vk-toggle': !showVirtualKeyboardToggle.value
            }]),
                    "math-mode-space": "\\;"
                  }, null, 8, ["class"]),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn-secondary",
                      onClick: onClose
                    }, _toDisplayString(_unref(layout)?.labels.cancel), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn-primary",
                      onClick: onConfirm
                    }, _toDisplayString(_unref(layout)?.labels.insert), 1)
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(operatorsS.value, (group, gIdx) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: gIdx,
                        class: "operators-group"
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group, (op, oIdx) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: oIdx,
                            type: "button",
                            class: "op-btn",
                            onClick: ($event: any) => (insertOperator(op[1]))
                          }, [
                            _createElementVNode("span", {
                              innerHTML: renderOp(op[0])
                            }, null, 8, _hoisted_8)
                          ], 8, _hoisted_7))
                        }), 128))
                      ]))
                    }), 128))
                  ])
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  _withDirectives(_createElementVNode("textarea", {
                    ref_key: "textareaRef",
                    ref: textareaRef,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((latexValue).value = $event)),
                    class: "latex-textarea",
                    placeholder: _unref(layout)?.helps.formula
                  }, null, 8, _hoisted_9), [
                    [_vModelText, latexValue.value]
                  ]),
                  _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(layout)?.labels.preview), 1),
                  _createElementVNode("div", {
                    class: "latex-preview",
                    innerHTML: latexHtml.value
                  }, null, 8, _hoisted_11),
                  (latexError.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(latexError.value), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn-secondary",
                      onClick: onClose
                    }, _toDisplayString(_unref(layout)?.labels.cancel), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn-primary",
                      onClick: onConfirm
                    }, _toDisplayString(_unref(layout)?.labels.insert), 1)
                  ])
                ], 64))
          ], 38)
        ]))
      : _createCommentVNode("", true)
  ], 8, ["to"]))
}
}

})